// Shared, cached access to the decoded library index for WebMCP tools.
//
// The library page already ships the compact index to the browser (Cache
// Storage + a search Worker). Agent tools reuse the exact same payload, so
// answering an agent costs no extra network once the page is warm.

import { decodeRow } from "@/lib/library-types";
import type { LibraryGem, LibraryIndex } from "@/lib/library-types";

const INDEX_URL = "/api/public/library";
const CACHE_NAME = "vibeary-index-v4";

export type LibraryData = {
  gems: LibraryGem[];
  categories: { name: string; count: number }[];
};

let cached: Promise<LibraryData> | null = null;

async function readCached(): Promise<LibraryIndex | null> {
  try {
    if (typeof caches === "undefined") return null;
    const cache = await caches.open(CACHE_NAME);
    const hit = await cache.match(INDEX_URL);
    return hit ? ((await hit.json()) as LibraryIndex) : null;
  } catch {
    return null;
  }
}

export function loadLibraryData(): Promise<LibraryData> {
  if (cached) return cached;
  cached = (async () => {
    let index = await readCached();
    if (!index) {
      const res = await fetch(INDEX_URL);
      if (!res.ok) throw new Error("Library index unavailable");
      index = (await res.json()) as LibraryIndex;
    }
    const gems = index.rows.map((row) =>
      decodeRow(row, index!.cats, index!.subs, index!.imgBase),
    );
    return { gems, categories: index.categories ?? [] };
  })();
  return cached;
}

/* ------------------------------- scoring -------------------------------- */

function norm(s: string): string {
  return s.toLowerCase().normalize("NFKD").replace(/[\u0300-\u036f]/g, "");
}

/** Lightweight relevance score mirroring the search Worker's intent. */
export function scoreGem(gem: LibraryGem, terms: string[]): number {
  if (terms.length === 0) return 1;
  const name = norm(gem.name);
  const desc = norm(gem.description);
  const tags = gem.tags.map(norm);
  const cat = norm(`${gem.category} ${gem.subcategory}`);
  const author = norm(gem.author ?? "");
  let score = 0;
  for (const term of terms) {
    let hit = 0;
    if (name === term) hit += 120;
    else if (name.startsWith(term)) hit += 80;
    else if (name.includes(term)) hit += 50;
    if (tags.some((t) => t === term)) hit += 40;
    else if (tags.some((t) => t.includes(term))) hit += 20;
    if (cat.includes(term)) hit += 15;
    if (author.includes(term)) hit += 12;
    if (desc.includes(term)) hit += 10;
    if (hit === 0) return 0; // every term must match somewhere
    score += hit;
  }
  return score;
}

export type GemFilter = {
  query?: string;
  category?: string;
  subcategory?: string;
  tag?: string;
  author?: string;
  license?: string;
  sort?: "relevance" | "name" | "views" | "likes" | "new";
  limit?: number;
  offset?: number;
};

export function filterGems(
  gems: LibraryGem[],
  f: GemFilter,
): { total: number; items: LibraryGem[] } {
  const terms = norm(f.query ?? "")
    .split(/\s+/)
    .filter(Boolean);
  const cat = f.category ? norm(f.category) : null;
  const sub = f.subcategory ? norm(f.subcategory) : null;
  const tag = f.tag ? norm(f.tag) : null;
  const author = f.author ? norm(f.author) : null;
  const license = f.license ? norm(f.license) : null;

  const scored: { gem: LibraryGem; score: number }[] = [];
  for (const gem of gems) {
    if (cat && norm(gem.category) !== cat && !norm(gem.category).includes(cat)) continue;
    if (sub && !norm(gem.subcategory).includes(sub)) continue;
    if (tag && !gem.tags.some((t) => norm(t) === tag)) continue;
    if (author && !norm(gem.author ?? "").includes(author)) continue;
    if (license && !norm(gem.license ?? "").includes(license)) continue;
    const score = scoreGem(gem, terms);
    if (score <= 0) continue;
    scored.push({ gem, score });
  }

  const sort = f.sort ?? "relevance";
  scored.sort((a, b) => {
    switch (sort) {
      case "name":
        return a.gem.name.localeCompare(b.gem.name);
      case "views":
        return b.gem.views - a.gem.views;
      case "likes":
        return b.gem.likes - a.gem.likes;
      case "new":
        return b.gem.addedDay - a.gem.addedDay;
      default:
        return b.score - a.score || b.gem.views - a.gem.views;
    }
  });

  const offset = Math.max(0, f.offset ?? 0);
  const limit = Math.min(Math.max(1, f.limit ?? 10), 50);
  return {
    total: scored.length,
    items: scored.slice(offset, offset + limit).map((s) => s.gem),
  };
}

export const SITE_ORIGIN = "https://vibing.inc";

/** Compact, agent-friendly projection of a gem. */
export function gemSummary(gem: LibraryGem) {
  return {
    slug: gem.slug,
    name: gem.name,
    description: gem.description,
    category: gem.category,
    subcategory: gem.subcategory,
    tags: gem.tags,
    author: gem.author || null,
    license: gem.license || null,
    likes: gem.likes,
    views: gem.views,
    page: `${SITE_ORIGIN}/library/${gem.slug}`,
  };
}
